import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { setAppVisible } from '@adrodd/core';
import { App } from './App';
import './globals.css';

// Initialize Sentry error monitoring (loaded only when a DSN is configured,
// so the module never lands in the eager entry chunk otherwise)
if (import.meta.env.VITE_SENTRY_DSN) {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE || 'production',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance monitoring sample rate (10% to stay within free tier)
      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
      // Session replay sample rate (10% for errors)
      replaysSessionSampleRate: 0.0, // Don't sample regular sessions
      replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE || '0.1'),
      // Don't send PII
      beforeSend(event) {
        // Remove sensitive data
        if (event.request) {
          delete event.request.cookies;
        }
        return event;
      },
      ignoreErrors: [
        // Browser extension errors
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        // Network errors
        'Network request failed',
        'Failed to fetch',
        // Common non-critical errors
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    });
  });
}

// The dashboard progress card replays its intro when the app is opened again
// (see @adrodd/core appOpen): every return to the tab, however short.
document.addEventListener('visibilitychange', () => setAppVisible(document.visibilityState === 'visible'));

// A data router, not <BrowserRouter>: only it can block a navigation (the
// reader's "Finished this chapter?" prompt, see ChapterLeavePrompt). App owns
// all the routes, so this is one catch-all route around it.
const router = createBrowserRouter([{ path: '*', element: <App /> }], {
  future: { v7_relativeSplatPath: true },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  </React.StrictMode>
);
